/**
 * Integration credentials as the API returns them: tokens, secrets and API keys are never sent to
 * the browser. Each one arrives as `<field>Set` (whether a value is stored) and, for a long enough
 * value, `<field>Last4`. Saving or testing with a secret field left blank keeps the stored value on
 * the server (`src/utils/integrationCredentials.ts` on the API).
 */

export interface SavedSecret {
  saved: boolean;
  last4?: string;
}

/** What the API says is stored for one write-only credential field. */
export function savedSecret(credentials: Record<string, unknown> | null | undefined, field: string): SavedSecret {
  const c = credentials || {};
  const last4 = c[`${field}Last4`];
  return {
    saved: c[`${field}Set`] === true,
    ...(typeof last4 === 'string' && last4 ? { last4 } : {}),
  };
}

/** The read-only display of a write-only field: "•••• 1234", "Saved", or undefined when nothing is stored. */
export function maskedSecret(
  credentials: Record<string, unknown> | null | undefined,
  field: string,
  savedLabel: string,
): string | undefined {
  const { saved, last4 } = savedSecret(credentials, field);
  if (!saved) return undefined;
  return last4 ? `•••• ${last4}` : savedLabel;
}

/**
 * Whether the caller may change, reconnect or test retyped integration credentials: an organization
 * owner or admin, or a platform admin. The API enforces the same rule (MOT-249); this only decides
 * what the UI offers.
 */
export function canManageIntegrationCredentials(organizationRole?: string | null, platformRole?: string | null): boolean {
  return (
    organizationRole === 'owner' ||
    organizationRole === 'admin' ||
    platformRole === 'admin' ||
    platformRole === 'super_admin'
  );
}

/**
 * True when the edit form's credentials differ from what the API returned for the integration, i.e.
 * the store typed something. Key order does not matter; a field cleared back to empty counts as
 * unchanged when it was absent before.
 */
export function credentialsEdited(
  loaded: Record<string, unknown> | null | undefined,
  current: Record<string, unknown> | null | undefined,
): boolean {
  const a = loaded || {};
  const b = current || {};
  const blank = (v: unknown) => v === undefined || v === null || v === '';
  for (const key of Array.from(new Set([...Object.keys(a), ...Object.keys(b)]))) {
    if (blank(a[key]) && blank(b[key])) continue;
    if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) return true;
  }
  return false;
}
